import React, { useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';
import { useCards, useCredits, useCreditPaymentSocket } from '../hooks';
import { CardFormFields, emptyCardForm } from './CardFormFields';
import { PixQrCodeDisplay } from './PixQrCodeDisplay';
import type { CardFormData } from './CardFormFields';
import type { CompanyCardResponse, CreditPaymentMethod, SubscriptionResult } from '../types';
import { CompanyEditDrawer } from '@/modules/settings/company/CompanyEditDrawer';

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  planName: string;
}

/**
 * Modal de assinatura de plano — Modal shared (F72).
 */
export const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  isOpen,
  onClose,
  planId,
  planName,
}) => {
  const { cards, isLoading: isLoadingCards } = useCards(isOpen);
  const { subscribeAsync, isSubscribing } = useCredits();
  const { confirmedPayment, resetConfirmedPayment } = useCreditPaymentSocket();

  const [paymentMethod, setPaymentMethod] = useState<CreditPaymentMethod>('credit_card');
  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [showNewCard, setShowNewCard] = useState(false);
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [pixResult, setPixResult] = useState<SubscriptionResult['pixData'] | null>(null);
  const [newCard, setNewCard] = useState<CardFormData>({ ...emptyCardForm });
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false);

  // Auto-select default card
  useEffect(() => {
    if (isOpen && cards && cards.length > 0 && selectedCardIndex === null) {
      const defaultIdx = cards.findIndex((c: CompanyCardResponse) => c.isDefault);
      setSelectedCardIndex(defaultIdx >= 0 ? defaultIdx : 0);
    }
  }, [isOpen, cards, selectedCardIndex]);

  // Reset filho CompanyEditDrawer ao fechar (ciclo pai→filho)
  useEffect(() => {
    if (!isOpen) {
      setCompanyDrawerOpen(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    resetConfirmedPayment();
    setPixResult(null);
    onClose();
  };

  const handleConfirm = async () => {
    try {
      const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
      const result = await subscribeAsync({
        planId,
        paymentMethod,
        cycle,
        ...(paymentMethod === 'credit_card' && !showNewCard && selectedCardIndex !== null
          ? { cardIndex: selectedCardIndex }
          : {}),
        ...(paymentMethod === 'credit_card' && showNewCard
          ? {
              newCard: {
                number: newCard.number,
                cvv: newCard.cvv,
                expiryMonth: parseInt(newCard.expiryMonth, 10),
                expiryYear: currentCentury + parseInt(newCard.expiryYear, 10),
                holderName: newCard.holderName,
                nickname: newCard.nickname || undefined,
                setAsDefault: true,
              },
            }
          : {}),
      });

      if (result.paymentStatus === 'pending' && result.pixData) {
        setPixResult(result.pixData);
      } else {
        handleClose();
      }
    } catch (error) {
      // Check if the error is about missing CPF/CNPJ — open company drawer
      if (error instanceof AxiosError) {
        const msg = error.response?.data?.message || error.response?.data?.error || '';
        if (typeof msg === 'string' && (msg.includes('CPF') || msg.includes('CNPJ'))) {
          setCompanyDrawerOpen(true);
        }
      }
      // Toast is already handled by the hook's onError
    }
  };

  const canConfirm = () => {
    if (paymentMethod === 'pix') return true;
    if (showNewCard) {
      return (
        newCard.holderName.trim() &&
        newCard.number.length === 16 &&
        newCard.expiryMonth.length === 2 &&
        newCard.expiryYear.length === 2 &&
        newCard.cvv.length === 3
      );
    }
    return selectedCardIndex !== null;
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} maxWidth="lg" closeOnEscape={!isSubscribing}>
        <div data-testid="payment-method-modal">
          <ModalHeader title="Assinar Plano" onClose={isSubscribing ? undefined : handleClose}>
            <span className="text-sm truncate" style={{ color: '#6b7280' }}>
              {planName}
            </span>
          </ModalHeader>

          <ModalContent className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto" padding={false}>
            {pixResult && confirmedPayment ? (
              /* Payment Confirmed */
              <div className="space-y-4 text-center" data-testid="payment-confirmed">
                <div className="flex justify-center">
                  <Icon name="check-circle" className="h-16 w-16" style={{ color: '#22c55e' }} />
                </div>
                <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                  Pagamento confirmado!
                </h3>
                <p className="text-sm" style={{ color: '#6b7280' }}>
                  {confirmedPayment.creditsPerCycle.toLocaleString('pt-BR')} créditos foram adicionados ao seu saldo.
                </p>
              </div>
            ) : pixResult ? (
              /* PIX Result - Waiting payment */
              <div className="space-y-4">
                <div className="flex justify-center">
                  <Icon name="clock" className="h-12 w-12" style={{ color: '#f59e0b' }} />
                </div>
                <h3 className="text-lg font-semibold text-center" style={{ color: '#1e293b' }}>
                  PIX gerado - aguardando pagamento
                </h3>
                <PixQrCodeDisplay
                  qrCode={pixResult.qrCode}
                  copyPaste={pixResult.copyPaste}
                  expiresAt={pixResult.expiresAt}
                />
              </div>
            ) : (
              <>
                {/* Cycle selection */}
                <div>
                  <label className="text-sm font-medium mb-2 block" style={{ color: '#1e293b' }}>
                    Ciclo de cobranca
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {(['monthly', 'yearly'] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCycle(c)}
                        className="p-3 rounded-lg text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: cycle === c ? '#f3e8ff' : '#f9fafb',
                          border: `2px solid ${cycle === c ? '#8b5cf6' : '#e5e7eb'}`,
                          color: cycle === c ? '#7c3aed' : '#374151',
                        }}
                        data-testid={`payment-cycle-${c}`}
                      >
                        {c === 'monthly' ? 'Mensal' : 'Anual'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment method selection */}
                <div>
                  <label className="text-sm font-medium mb-2 block" style={{ color: '#1e293b' }}>
                    Método de pagamento
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('credit_card')}
                      className="flex items-center gap-2 p-3 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: paymentMethod === 'credit_card' ? '#f3e8ff' : '#f9fafb',
                        border: `2px solid ${paymentMethod === 'credit_card' ? '#8b5cf6' : '#e5e7eb'}`,
                        color: paymentMethod === 'credit_card' ? '#7c3aed' : '#374151',
                      }}
                      data-testid="payment-method-credit-card"
                    >
                      <Icon name="credit-card" className="h-4 w-4" />
                      Cartao
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('pix')}
                      className="flex items-center gap-2 p-3 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: paymentMethod === 'pix' ? '#f3e8ff' : '#f9fafb',
                        border: `2px solid ${paymentMethod === 'pix' ? '#8b5cf6' : '#e5e7eb'}`,
                        color: paymentMethod === 'pix' ? '#7c3aed' : '#374151',
                      }}
                      data-testid="payment-method-pix"
                    >
                      <Icon name="smartphone" className="h-4 w-4" />
                      PIX
                    </button>
                  </div>
                </div>

                {/* Card selection (if credit_card) */}
                {paymentMethod === 'credit_card' && (
                  <div className="space-y-3">
                    {isLoadingCards ? (
                      <div className="animate-pulse space-y-2">
                        {[1, 2].map((i) => (
                          <div
                            key={i}
                            className="h-14 rounded-lg"
                            style={{ backgroundColor: '#f3f4f6' }}
                          />
                        ))}
                      </div>
                    ) : (
                      <>
                        {/* Saved cards */}
                        {cards && cards.length > 0 && !showNewCard && (
                          <div className="space-y-2">
                            {cards.map((card: CompanyCardResponse, idx: number) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setSelectedCardIndex(idx)}
                                className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors"
                                style={{
                                  backgroundColor: selectedCardIndex === idx ? '#f3e8ff' : '#f9fafb',
                                  border: `2px solid ${selectedCardIndex === idx ? '#8b5cf6' : '#e5e7eb'}`,
                                }}
                                data-testid={`payment-card-${idx}`}
                              >
                                <Icon name="credit-card" className="h-5 w-5 flex-shrink-0" style={{ color: '#6b7280' }} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                                    {card.brand} **** {card.maskedNumber.slice(-4)}
                                  </p>
                                  <p className="text-xs" style={{ color: '#9ca3af' }}>
                                    {card.holderName} - {String(card.expiryMonth).padStart(2, '0')}/{card.expiryYear}
                                  </p>
                                </div>
                                {card.isDefault && (
                                  <span
                                    className="text-xs px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}
                                  >
                                    Padrao
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Toggle add new */}
                        {!showNewCard ? (
                          <button
                            type="button"
                            onClick={() => {
                              setShowNewCard(true);
                              setSelectedCardIndex(null);
                            }}
                            className="w-full flex items-center gap-2 p-3 rounded-lg text-sm transition-colors"
                            style={{
                              border: '2px dashed #e5e7eb',
                              color: '#6b7280',
                            }}
                            data-testid="payment-add-new-card"
                          >
                            <Icon name="plus" className="h-4 w-4" />
                            Adicionar novo cartao
                          </button>
                        ) : (
                          /* New card form */
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                                Novo cartao
                              </span>
                              {cards && cards.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowNewCard(false);
                                    setNewCard({ ...emptyCardForm });
                                    const defaultIdx = cards.findIndex((c: CompanyCardResponse) => c.isDefault);
                                    setSelectedCardIndex(defaultIdx >= 0 ? defaultIdx : 0);
                                  }}
                                  className="text-xs"
                                  style={{ color: '#8b5cf6' }}
                                >
                                  Usar cartao salvo
                                </button>
                              )}
                            </div>
                            <CardFormFields card={newCard} onChange={setNewCard} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </ModalContent>

          <ModalFooter>
            {pixResult ? (
              <Button variant="primary" onClick={handleClose} data-testid="payment-close">
                Fechar
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isSubscribing}
                  data-testid="payment-cancel"
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirm}
                  disabled={!canConfirm() || isSubscribing}
                  data-testid="payment-submit"
                >
                  {isSubscribing
                    ? 'Processando...'
                    : paymentMethod === 'pix'
                      ? 'Gerar PIX'
                      : 'Confirmar Assinatura'}
                </Button>
              </>
            )}
          </ModalFooter>
        </div>
      </Modal>

      <CompanyEditDrawer
        isOpen={companyDrawerOpen}
        onClose={() => setCompanyDrawerOpen(false)}
      />
    </>
  );
};

PaymentMethodModal.displayName = 'PaymentMethodModal';
